import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Button from '../../../components/Form/Button'
import Input from '../../../components/Form/Input'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import { CustomContentModal } from '../../../components/CustomContentModal'
import ModifiedDescriptionField from '../programOnboarding/ModifiedDescriptionField'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import type { ProgramMeterOption, ProgramMeterRow } from '../../../apis/types'
import { createProgramMeterApi, updateProgramMeterApi } from '../../../apis/apis'
import { getApiErrorMessage } from '../../../apis/apiError'
import { isEndDateOnOrAfterStart, NO_PLANNED_END_DATE } from '../../../util/string'

interface MeterFormValues {
  benefittingMeterCuid: string
  startDate: string
  endDate: string
  modifiedDescription: string
}

const requiredMessage = 'Required'
const FALLBACK_ERROR = 'Something went wrong. Please try again.'

const buildValidationSchema = (mode: 'create' | 'edit') =>
  Yup.object({
    benefittingMeterCuid: mode === 'create' ? Yup.string().required(requiredMessage) : Yup.string(),
    startDate: Yup.string().required(requiredMessage),
    endDate: Yup.string().test('end-after-start', 'End date cannot be before start date', function validate(value) {
      if (value == null || value === '') return true
      const { startDate } = this.parent as MeterFormValues
      if (startDate == null || startDate === '') return true
      return isEndDateOnOrAfterStart(value, startDate)
    }),
    modifiedDescription: Yup.string().required(requiredMessage),
  })

interface ProgramMeterDatesModalProps {
  isOpen: boolean
  mode: 'create' | 'edit'
  meterOptions: ProgramMeterOption[]
  editingRow: ProgramMeterRow | null
  initialBenefittingMeterCuid?: string
  onClose: () => void
  onSaved: () => void
  programId: string
}

const ProgramMeterDatesModal: React.FC<ProgramMeterDatesModalProps> = ({
  isOpen,
  mode,
  meterOptions,
  editingRow,
  initialBenefittingMeterCuid,
  onClose,
  onSaved,
  programId,
}) => {
  const dispatch = useAppDispatch()
  const [apiError, setApiError] = useState<string | null>(null)

  const meterComboData = meterOptions.map((m) => ({
    id: m.cuid,
    name: m.meterNumber,
    description: m.serviceAddress ?? undefined,
  }))

  const formik = useFormik<MeterFormValues>({
    initialValues: {
      benefittingMeterCuid: editingRow?.benefittingMeter.cuid ?? initialBenefittingMeterCuid ?? '',
      startDate: editingRow?.startDate ?? '',
      endDate: editingRow?.endDate ?? NO_PLANNED_END_DATE,
      modifiedDescription: '',
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(mode),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      setApiError(null)
      setSubmitting(true)
      const endDate = values.endDate === '' ? null : values.endDate
      try {
        if (mode === 'create') {
          await createProgramMeterApi(programId, {
            benefittingMeterCuid: values.benefittingMeterCuid,
            startDate: values.startDate,
            endDate,
            modifiedDescription: values.modifiedDescription,
          })
          await dispatch(pushToast({ type: 'success', message: 'Program meter added' }))
        } else if (editingRow != null) {
          await updateProgramMeterApi(programId, editingRow.cuid, {
            startDate: values.startDate,
            endDate,
            modifiedDescription: values.modifiedDescription,
          })
          await dispatch(pushToast({ type: 'success', message: 'Program meter updated' }))
        }
        onSaved()
        onClose()
      } catch (e: unknown) {
        setApiError(getApiErrorMessage(e, FALLBACK_ERROR))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (isOpen) {
      setApiError(null)
      void formik.resetForm({
        values: {
          benefittingMeterCuid: editingRow?.benefittingMeter.cuid ?? initialBenefittingMeterCuid ?? '',
          startDate: editingRow?.startDate ?? '',
          endDate: editingRow?.endDate ?? NO_PLANNED_END_DATE,
          modifiedDescription: '',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingRow?.cuid, initialBenefittingMeterCuid])

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-4 text-xl font-medium dark:text-white">
          {mode === 'create' ? 'Add program meter' : 'Edit program meter'}
        </h2>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          {mode === 'create' ? (
            <>
              <ComboboxAutocomplete
                label="Benefitting meter"
                data={meterComboData}
                value={formik.values.benefittingMeterCuid || null}
                handleChange={(value) => formik.setFieldValue('benefittingMeterCuid', value ?? '')}
                wildCardDisabled
                isNotFilter
                disabled={initialBenefittingMeterCuid != null && initialBenefittingMeterCuid !== ''}
              />
              {formik.errors.benefittingMeterCuid != null && (
                <p className="text-xs text-red-600 dark:text-red-500 -mt-2">{formik.errors.benefittingMeterCuid}</p>
              )}
            </>
          ) : (
            <div className="text-sm">
              <div className="font-medium text-slate-700 dark:text-slate-400 mb-1">Benefitting meter</div>
              <div className="dark:text-white">{editingRow?.benefittingMeter.meterNumber}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start date"
              type="date"
              required
              value={formik.values.startDate}
              onChange={(e) => formik.setFieldValue('startDate', e.target.value)}
              error={formik.errors.startDate}
            />
            <Input
              label="End date"
              type="date"
              description={`Use ${NO_PLANNED_END_DATE} for no planned end`}
              value={formik.values.endDate}
              onChange={(e) => formik.setFieldValue('endDate', e.target.value)}
              error={formik.errors.endDate}
            />
          </div>

          <ModifiedDescriptionField
            value={formik.values.modifiedDescription}
            onChange={(value) => formik.setFieldValue('modifiedDescription', value)}
            error={formik.errors.modifiedDescription}
          />

          {apiError != null && <p className="text-sm text-red-600 dark:text-red-500">{apiError}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isSaving={formik.isSubmitting}>
              {mode === 'create' ? 'Add meter' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </CustomContentModal>
  )
}

export default ProgramMeterDatesModal
